import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "~/lib/utils";

// Shared by the link-family and "inline" compoundVariants below.
const CHROMELESS_BUTTON = "h-auto rounded-none p-0";

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-[15px] font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        highlight:
          "bg-primary-foreground text-primary brightness-98 hover:brightness-94 text-sm font-semibold",
        inverse: "bg-brand-darker text-brand-yellow hover:bg-brand-darker/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border-[1.5px] bg-background text-brand-darker hover:border-brand-darker hover:text-accent-foreground disabled:bg-brand-darker/[0.03] disabled:border-brand-darker/20 disabled:text-brand-darker/60 disabled:opacity-100 dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline underline-offset-4 hover:text-brand-darker",
        linkError:
          "text-status-danger underline underline-offset-4 hover:text-status-danger-strong",
        linkSuccess:
          "text-status-success underline underline-offset-4 hover:text-status-success-strong",
        linkMuted:
          "text-brand-dark-muted underline underline-offset-4 hover:text-brand-dark",
        inline: "",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 lg:h-12 px-6 has-[>svg]:px-4",
        xl: "px-6 py-4 has-[>svg]:px-6 max-md:h-11",
        icon: "size-9",
      },
    },
    compoundVariants: [
      // Link variants render like an inline <a>: drop the button sizing/chrome.
      {
        variant: ["link", "linkError", "linkSuccess", "linkMuted"],
        className: `${CHROMELESS_BUTTON} inline text-[1em] whitespace-normal`,
      },
      // Same chromeless sizing, but keeps the base's inline-flex + gap so an
      // icon and label still lay out in a row instead of stacking.
      {
        variant: ["inline"],
        className: `${CHROMELESS_BUTTON} has-[>svg]:px-0`,
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
